import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Coordinate, CoordinateObject, PolygonCoordinates, Ring } from '../../../types/geo';
import MapFieldHole from '../MapObjects/MapFieldHole';
import { getFieldsArray } from '../ManageCrops/ManageCrops';
import MapPolygon from '../MapObjects/MapPolygon';
import FieldTextCanvas from '../ManageCrops/FieldTextCanvas';
import { getFieldName } from '../../helpers/StateInterpreters';
import { useLangFileRef } from '../../context/LanguageContext';
import MapOverlay from '../MapObjects/MapOverlay';
import { Box } from '@mui/material';
import { selectField, setDate, setDates } from '../../reducers/FieldReducer';
import { isSatelliteLayer, isSurveyLayer, Survey } from '../Prescription/PrescriptionJob';
import { setViewMode } from '../../reducers/ControlReducer';
import ViewModeConstants from '../../constants/ViewModeConstants';
import { CropTypeColor } from '../../constants/CropConstants';
import ListOutlined from '@mui/icons-material/ListOutlined';
import MenuPopoverButton from '../MenuButton/MenuPopoverButton';
import MapLegendContainer from '../MapLegend/MapLegendContainer';
import BulkPrescriptionFieldOverviewContainer from './BulkPrescriptionFieldOverview/BulkPrescriptionFieldOverviewContainer';
import BulkPrescriptionConfirmActionPopover from './BulkPrescriptionConfirmActionPopover';
import { handleSatelliteLayer, handleSurveyLayer } from './BulkUtils';
import { getClickedFields } from '../../helpers/MapsUtils';
import DuplicateFieldsDialog from '../MapView/DuplicateFieldsDialog';
import StyleConstants from '../../StyleConstants';
import {
  resetBulkPrescriptions,
  setBulkPrescriptionFields,
} from '../../reducers/PrescriptionReducer';
import type Field from '../../model/Field';
import { SATELLITE_LAYERS } from '../../constants/SatelliteLayers';

interface BulkPrescriptionViewProps extends ConnectedProps<typeof connector> {
  getFieldImage: (field: Field) => string | null;
  imageType: string;
}

const BulkPrescriptionView: React.FC<BulkPrescriptionViewProps> = ({
  selectedField,
  fields,
  getFieldImage,
  imageType,
  date,
  selectedLayer,
  surveys,
  filteredFields,
  dispatch,
}) => {
  const LangFileRef = useLangFileRef();
  const [hoveredField, setHoveredField] = useState<Field | null>(null);
  const [selectedFields, setSelectedFields] = useState<Field[]>(
    filteredFields.size === 0 && selectedField
      ? [selectedField]
      : Array.from(filteredFields.values())
  );
  const [errorFields, setErrorFields] = useState<Field[]>([]);
  const [duplicateFields, setDuplicateFields] = useState<Field[] | null>(null);
  const [fetchingDates, setFetchingDates] = useState(false);
  const datesCacheRef = useRef<Map<number, string[]>>(new Map());
  const isMountedRef = useRef(true);

  const toggleField = (clickedField: Field): void => {
    if (selectedFields.includes(clickedField)) {
      const newSelectedFields = selectedFields.filter((f) => f.fieldId !== clickedField.fieldId);
      const newErrorFields = errorFields.filter((f) => f.fieldId !== clickedField.fieldId);

      setErrorFields(newErrorFields);
      setSelectedFields(newSelectedFields);
    } else {
      setSelectedFields([...selectedFields, clickedField]);
    }
  };

  const handleFieldClick = (field: Field, event: any): void => {
    const clicked = getClickedFields(fields, event);

    if (clicked.length > 1) {
      setDuplicateFields(clicked);
    } else {
      toggleField(field);
    }
  };

  const fieldsArray = getFieldsArray(fields);
  const fieldMapPolygons = fieldsArray.map((field, idx) => {
    const coordinates: CoordinateObject[] = field.polygon.coordinates.map((ring: Ring) => {
      return ring.map((coord: Coordinate) => {
        return {
          lng: coord[0],
          lat: coord[1],
        };
      });
    });

    const visibleFieldHoles: PolygonCoordinates = field.polygon.coordinates.slice(1);

    const onMouseEnter = () => {
      setHoveredField(field);
    };

    const onMouseLeave = () => {
      setHoveredField(null);
    };

    const fillColor = 'white';
    let strokeColor = 'rgb(224,210,61)';
    let strokeWeight = 2;

    if (hoveredField === field) {
      strokeColor = StyleConstants.palette.primary.main;
      strokeWeight = 4;
    } else if (selectedFields.includes(field)) {
      strokeColor = 'white';
      strokeWeight = 6;
    }

    const fieldImage = getFieldImage(field);

    return (
      <React.Fragment key={field.fieldId}>
        <MapPolygon
          key={idx}
          fillOpacity={0.0}
          fillColor={fillColor}
          strokeColor={strokeColor}
          strokeWeight={strokeWeight}
          disabled={false}
          coordinates={coordinates}
          onClick={(event) => handleFieldClick(field, event)}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
        {visibleFieldHoles &&
          visibleFieldHoles.map((boundary, index) => (
            <MapFieldHole fieldHole={boundary} key={index} fieldHoleDisabled={true} />
          ))}
        {fieldImage && <MapOverlay zIndex={100} fieldImage={fieldImage} imageType={imageType} />}
      </React.Fragment>
    );
  });

  const renderFieldText = useCallback((field: Field, zoom: number) => {
    const lines: {
      text: string;
      fontWeight: string;
      fontSize: number;
      textAlign: string;
      color: string;
    }[] = [];

    if (zoom >= 14) {
      // Name
      lines.push({
        text: getFieldName(field, LangFileRef.current),
        fontWeight: '400',
        fontSize: 14,
        textAlign: 'center',
        color: 'white',
      });

      // Crop
      const cropText = LangFileRef.current.CropType[field.crop];

      lines.push({
        text: cropText,
        fontWeight: '900',
        fontSize: 14,
        textAlign: 'center',
        color: 'white',
      });

      // Crop Variation
      if (field.cropVariation != null && field.cropVariation.length > 0) {
        const cropVariationText = field.cropVariation;

        lines.push({
          text: cropVariationText,
          fontWeight: '900',
          fontSize: 14,
          textAlign: 'center',
          color: 'white',
        });
      }
    }

    return lines;
  }, []);

  const intersectDates = (arrays: string[][]): string[] => {
    if (arrays.length === 0) return [];

    return arrays.reduce((acc, curr) => acc.filter((date) => curr.includes(date)));
  };

  const ensureSelectedField = (selectedFields: Field[], selectedField: Field) => {
    const isSelected = selectedFields.some(
      (f) => f.fieldId === (selectedField && selectedField.fieldId)
    );

    if (!isSelected) {
      dispatch(selectField(selectedFields.length > 0 ? selectedFields[0] : null));
    }
  };

  useEffect(() => {
    ensureSelectedField(selectedFields, selectedField, dispatch);
  }, [selectedFields, selectedField, dispatch]);

  useEffect(() => {
    if (!selectedFields.length) return;
    if (!isSatelliteLayer(selectedLayer)) return;

    setFetchingDates(true);

    const fetchSatelliteData = async () => {
      const updatedFields = await handleSatelliteLayer({
        selectedFields,
        date,
        datesCacheRef,
        dispatch,
      });

      if (!isMountedRef.current) return;

      setErrorFields(updatedFields.filter((f) => f.error));
      setFetchingDates(false);
    };

    fetchSatelliteData();
  }, [selectedFields, selectedLayer, date, dispatch]);

  useEffect(() => {
    if (!selectedFields.length) return;
    if (!isSurveyLayer(selectedLayer)) return;

    const updatedFields = handleSurveyLayer(selectedFields, surveys, selectedLayer);
    setErrorFields(updatedFields.filter((f) => f.error));
  }, [selectedFields, selectedLayer, surveys]);

  useEffect(() => {
    // Prevent state updates on unmounted component
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      <Box
        style={{ pointerEvents: 'none' }}
        sx={{
          zIndex: 2,
        }}
      >
        <BulkPrescriptionConfirmActionPopover
          selectedFields={selectedFields}
          errorFields={errorFields}
          fetchingDates={fetchingDates}
          LangFile={LangFileRef.current}
          CropTypeColor={CropTypeColor}
          getFieldName={getFieldName}
          onConfirm={() => {
            dispatch(
              setBulkPrescriptionFields(
                selectedFields.map((f) => ({
                  ...f,
                  imageUrl: isSatelliteLayer(selectedLayer)
                    ? {
                        [SATELLITE_LAYERS.VITALITY_NDVI]:
                          getFieldImage(f)[SATELLITE_LAYERS.VITALITY_NDVI],
                        [SATELLITE_LAYERS.VARIATIONS_NDVI]:
                          getFieldImage(f)[SATELLITE_LAYERS.VARIATIONS_NDVI],
                        [SATELLITE_LAYERS.VISIBLE]: getFieldImage(f)[SATELLITE_LAYERS.VISIBLE],
                      }
                    : getFieldImage(f).url,
                }))
              )
            );
            dispatch(setViewMode(ViewModeConstants.PRESCRIPTION));
            dispatch(selectField(selectedFields[0]));
          }}
          onCancel={() => {
            dispatch(resetBulkPrescriptions());
            dispatch(setViewMode(ViewModeConstants.OVERVIEW));
          }}
          onRemoveField={(field) =>
            setSelectedFields(selectedFields.filter((f) => f.fieldId !== field.fieldId))
          }
        />

        <Box style={{ marginTop: '22%', marginLeft: '16px' }}>
          <MenuPopoverButton
            testId="bulk-field-list-open"
            ref={null}
            icon={<ListOutlined />}
            disableClickAway={true}
            buttonText={LangFileRef.current.FieldDrawer.buttonText}
            tooltip={LangFileRef.current.FieldDrawer.tooltip}
            onClick={() => null}
            renderChildren={(onClose) => (
              <BulkPrescriptionFieldOverviewContainer
                fields={fieldsArray}
                selectedFields={selectedFields}
                setSelectedFields={setSelectedFields}
                onClose={onClose}
              />
            )}
          />
          <MapLegendContainer />
        </Box>
      </Box>

      {fieldMapPolygons}
      <FieldTextCanvas fields={fieldsArray} renderField={renderFieldText} />

      {duplicateFields && (
        <DuplicateFieldsDialog
          isBulkSelecting={true}
          surveys={surveys}
          duplicateFields={duplicateFields}
          selectedFields={selectedFields}
          onFieldSelect={(field) => {
            toggleField(field);
            setDuplicateFields(null);
          }}
          onClose={() => setDuplicateFields(null)}
        />
      )}
    </>
  );
};

const mapStoreToProps = (store: any) => ({
  selectedField: store.field.selectedField,
  fields: store.field.fields,
  dates: store.field.dates,
  date: store.field.date?.date,
  selectedLayer: store.survey.selectedLayer,
  surveys: store.survey.surveys,
  filteredFields: store.field.filteredFields,
});

const connector = connect(mapStoreToProps);
export default memo(connector(BulkPrescriptionView));
